import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useParams} from "react-router";
import useStepperStateCloseValidator from "hooks/useStepperStateCloseValidator";
import RouterPath from "libs/RouterPath";
import useSnackbar from "hooks/useSnackbar";
import Tr from "translations/Translations"
import Loading from "components/Loading";
import Form from "components/Form";
import VS from "constants/ValidationShema";
import SaveFormikValues from "components/SaveFormikValues";
import PurchasingGroupStage2ScheduleForm from "./PurchasingGroupStage2ScheduleForm";
import moment from "moment";
import PurchasingGroupSectionService from "services/PurchasingGroupSectionService";
import DemandValidationMessages from "components/DemandValidationMessages";
import {PurchasingGroupStageSections} from "../../../constants/PurchasingGroupStageSections";
import PurchasingGroupStage2Service from "services/PurchasingGroupStage2Service";
import scheduleReloadTimeout from "../../../utils/scheduleReloadTimeout";

function PurchasingGroupStage2Schedule(
    {
        panelRef,
        validationSection
    }) {

    const params = useParams();
    const groupId = params.id;
    const formId = params.section;
    const editable = validationSection.editable;
    const {enqueueSuccessSnackbar, enqueueErrorSnackbar} = useSnackbar();
    const [schedule, setSchedule] = useState(null);
    const timeout = useRef(0);

    useStepperStateCloseValidator(formId);

    const closeSection = useCallback(() => RouterPath.redirectWithoutHistory(RouterPath.createPath(RouterPath.purchasingGroup.MP_PURCHASING_GROUP_GROUP_ID_STAGE_NR, {
        id: groupId,
        stage: 2
    })), [groupId]);

    const getSchedule = () => {
        setSchedule(null);
        if (timeout.current) {
            clearTimeout(timeout.current);
            timeout.current = 0;
        }
        PurchasingGroupStage2Service.getCollectingSchedule(groupId).then((resp) => {
            if (resp) {
                setSchedule(resp);
                let reloadTimeout = scheduleReloadTimeout(resp.schedule, resp.serverTime);
                if (reloadTimeout) {
                    timeout.current = setTimeout(() => {
                        getSchedule();
                    }, reloadTimeout)
                }

            } else
                closeSection();
        });
    }

    useEffect(() => {
        getSchedule();
        PurchasingGroupSectionService.getSections(groupId, validationSection.section);
        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
                timeout.current = 0;
            }
        }
        // eslint-disable-next-line
    }, [groupId])


    const handleSubmit = (values) => {
        PurchasingGroupStage2Service.saveCollectingSchedule(groupId, values).then((resp) => {
            if (resp && resp.success === false) {
                Object.keys(resp.errors).forEach((field) => {
                    let error;
                    switch (field) {
                        case "swzOpinionEndDate":
                            error = `${Tr('purchasing_group_opinion_swz_stage')} ${Tr('purchase_group_stage_end_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "swzOpinionStartDate":
                            error = `${Tr('purchasing_group_opinion_swz_stage')}: ${Tr('purchase_group_stage_start_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "collectingDataEndDate":
                            error = `${Tr('purchasing_group_collecting_data_stage')}: ${Tr('purchase_group_stage_end_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "collectingDataStartDate":
                            error = `${Tr('purchasing_group_collecting_data_stage')}: ${Tr('purchase_group_stage_start_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        default:
                            error = `${resp.errors[field].join(", ")}`
                    }
                    enqueueErrorSnackbar(error)
                })

            } else if (resp) {
                enqueueSuccessSnackbar(Tr("saved") + " " + validationSection.name);
                PurchasingGroupSectionService.getSections(groupId, [validationSection.section, PurchasingGroupStageSections.STAGE_2_SECTION])
                closeSection();
            }

        })
    }

    if (!schedule) return <Loading/>

    return (<div>
            <DemandValidationMessages editable={validationSection.editable} section={validationSection}/>
            <Form
                id={formId}
                initialValues={{
                    swzOpinionStartDate: schedule.schedule.swzOpinionStartDate ? moment(schedule.schedule.swzOpinionStartDate) : null,
                    swzOpinionEndDate: schedule.schedule.swzOpinionEndDate ? moment(schedule.schedule.swzOpinionEndDate) : null,
                    collectingDataStartDate: schedule.schedule.collectingDataStartDate ? moment(schedule.schedule.collectingDataStartDate) : null,
                    collectingDataEndDate: schedule.schedule.collectingDataEndDate ? moment(schedule.schedule.collectingDataEndDate) : null,
                }}
                validationSchema={VS.lazy((form) => {
                    const shape = {}
                    if (schedule.swzOpinionStartDateEditable) {
                        if (!form.swzOpinionEndDate)
                            shape.swzOpinionStartDate = VS.dateTimeMinNoRq(new Date(), Tr('v_time_can_not_be_past'))
                        else
                            shape.swzOpinionStartDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
                    }
                    if (schedule.swzOpinionEndDateEditable) {
                        const swzOpinionStartDate = form.swzOpinionStartDate ? form.swzOpinionStartDate.clone() : moment();
                        swzOpinionStartDate.add(1, "minute")

                        if (!form.swzOpinionStartDate)
                            shape.swzOpinionEndDate = VS.dateTimeMinNoRq(swzOpinionStartDate, Tr('v_end_time_can_not_be_before_start'))
                        else
                            shape.swzOpinionEndDate = VS.dateTimeMin(swzOpinionStartDate, Tr('v_end_time_can_not_be_before_start'))
                    }

                    if (schedule.collectingDataStartDateEditable) {
                        if(!form.collectingDataEndDate)
                            shape.collectingDataStartDate = VS.dateTimeMinNoRq(new Date(), Tr('v_time_can_not_be_past'))
                        else
                            shape.collectingDataStartDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
                    }
                    if (schedule.collectingDataEndDateEditable) {
                        const collectingDataStartDate = form.collectingDataStartDate ? form.collectingDataStartDate.clone() : moment();
                        collectingDataStartDate.add(1, "minute")

                        if (!form.collectingDataStartDate) {
                            shape.collectingDataEndDate = VS.dateTimeMinNoRq(collectingDataStartDate, Tr('v_end_time_can_not_be_before_start'))
                        } else {
                            shape.collectingDataEndDate = VS.dateTimeMin(collectingDataStartDate, Tr('v_end_time_can_not_be_before_start'))
                        }
                    }

                    return VS.object(shape)
                })}
                onSubmit={handleSubmit}
            >
                {(formikProps) => (
                    <>
                        <SaveFormikValues
                            formikProps={formikProps}
                            id={formId}
                        />
                        <PurchasingGroupStage2ScheduleForm editable={editable} schedule={schedule} formikProps={formikProps}/>
                        {/*<Typography style={{lineBreak: "anywhere"}}>{JSON.stringify(schedule)}</Typography>*/}
                    </>)}
            </Form>
        </div>
    )
}

PurchasingGroupStage2Schedule.propTypes = {}

export default PurchasingGroupStage2Schedule;